import {asString, defaultParamMap} from '@imt/vue-list/src/utils/queryParams';
import utils, {axiosWithAuth} from '@imt/vue-toolbox/src/utils';
import Vue from 'vue';

import wording from '@/utils';

export const authedAxios = axiosWithAuth();

authedAxios.interceptors.request.use(request => {
    // always force test mode by adding ?test_set=true to the url so the `test_db` is always used from Fake Client
    if (process.env.VUE_APP_FORCE_PD_DEFAULT_DB !== 'true') {
        request.params = { ...request.params, test_set: 'true' };
    }

    return request;
});

export const actions = {
    async duplicatePolicy(_, {
        data,
        id,
    }) {
        const url = `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policies/${id}/duplicate-as-quote/?include=versions&fields[Version]=policy_system_id`,
            response = await authedAxios.post(url, utils.dataFormatter.serialize({stuff: {type: 'Policy', ...data}}));

        return utils.dataFormatter.deserialize(response.data);
    },
    async createPolicyGroup(_, {snapshotData}) {
        const groupResponse = await authedAxios.post(
            `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policy-groups/`,
            utils.dataFormatter.serialize({stuff: snapshotData})
        );

        return utils.dataFormatter.deserialize(groupResponse.data);
    },
    async createPolicy({dispatch}, {data, snapshotData, groupId}) {
        let resolvedGroupId = groupId;

        if (!resolvedGroupId) {
            const group = await dispatch('createPolicyGroup', {snapshotData});

            resolvedGroupId = group.policyGroupId;
        }

        const response = await authedAxios.post(
            `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/data/?include=versions`,
            utils.dataFormatter.serialize({
                stuff: {
                    ...data,
                    groupId: resolvedGroupId,
                },
                includeNames: [
                    'versions',
                    'versions.entity',
                    'versions.updated_by',
                    'versions.created_by',
                ],
            }),
            // This is request is made as application/json rather than JSONAPI, even though the data is formatted as such, because
            // saving multiple models (as we are doing with the `includes` key) is out of spec. Our API plugin attempts to correct
            // our request data and we lose data. application/json accepts the data as is
            {headers: {'Content-Type': 'application/json'}},
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async uploadPolicy(_, {data, overwrite = false}) {
        const response = await authedAxios.post(
            `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/data/?include=versions&overwrite=${overwrite}`,
            data,
            // This is request is made as application/json rather than JSONAPI, even though the data is formatted as such, because
            // saving multiple models (as we are doing with the `includes` key) is out of spec. Our API plugin attempts to correct
            // our request data and we lose data. application/json accepts the data as is
            {headers: {'Content-Type': 'application/json'}},
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchOptions(_, { modelName, fieldName }) {
        const response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/options/${modelName}/${fieldName}/`);

        return response?.data?.data || [];
    },
    async fetchPolicies({commit}, queryObject) {
        let queryString = asString(queryObject, {
                ...defaultParamMap,
                ...wording.searchParams,
                search: 'search',
            }),
            url = `/api/v1/policies/?include=versions&fields[Version]=state,processing_status,coverage_status,transaction_type,term_effective_date,policy_system_id`,
            response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}${url}${queryString}`);

        commit('SET_DATA', {
            field: 'policies',
            data: utils.dataFormatter.deserialize(response.data),
        });

        commit('SET_DATA', {field: 'totalPolicyCount', data: response.data.meta.pagination.count});

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPolicyGroups({commit}, queryObject) {
        let queryString = asString(queryObject, {
                ...defaultParamMap,
                ...wording.searchParams,
                search: 'search',
            }),
            response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policy-groups/?${queryString}`);

        commit('SET_DATA', {
            field: 'policyGroups',
            data: utils.dataFormatter.deserialize(response.data),
        });

        commit('SET_DATA', {field: 'totalGroupCount', data: response.data.meta.pagination.count});

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPolicyById(context, policyId) {
        let url = `/api/v1/policies/${policyId}/?include=versions`,
            response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}${url}`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPolicyGroupById(context, {policyGroupId, getRenewalGroup}) {
        try {
            const response = await authedAxios.get(
                `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policy-groups/${policyGroupId}/?include=inspection_contact,insured_contact,mailing_address,named_insureds.entities,named_insureds.omits,policies,premium_audit_contact${getRenewalGroup ? '&renewal_group=true' : ''}`
            );

            return utils.dataFormatter.deserialize(response.data);
        } catch (error) {
            if (getRenewalGroup && error.response?.status === 404) {
                return null;
            }

            throw error;
        }
    },
    async savePolicyGroup(context, data) {
        const response = await authedAxios.patch(
            `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policy-groups/${data.id}/`,
            utils.dataFormatter.serialize({stuff: {...data, type: 'PolicyGroup'}}),
        );

        return utils.dataFormatter.deserialize(response.data);
    },
};

const mutations = {
    ADD_VERSION(state, {
        policy,
        version,
    }) {
        let statePolicy = state.policies.find(p => p.id === policy.id);
        statePolicy.versions.push(version);
    },
    SET_DATA(state, {field, data}) {
        state[field] = data;
    },
    CLEAR_SEARCH_FIELDS(state) {
        state.searchFields = {};
    },
    SET_SEARCH_FIELDS(state, searchField) {
        if ((typeof searchField.value !== 'boolean' && !searchField.value) || (Array.isArray(searchField.value) && !searchField.value.length)) {
            Vue.delete(state.searchFields, searchField.key);
        } else {
            Vue.set(state.searchFields, searchField.key, searchField.value);
        }
    },
    REMOVE_VERSION(state, {
        policy,
        versionId,
    }) {
        let statePolicy = state.policies.find(p => p.id === policy.id);

        Vue.delete(statePolicy.versions, statePolicy.versions.findIndex(v => v.id === versionId));
    },
};


export const state = () => {
    return {
        policies: [],
        policyGroups: [],
        totalGroupCount: 0,
        totalPolicyCount: 0,
        searchFields: {},
    };
};

export default {
    actions,
    getters: {},
    mutations,
    namespaced: true,
    state: state(),
};
